import React, { useEffect } from "react";

import { REQUEST_STATUS } from "@src/constants";
import { EditorButton } from "@src/features/resource_plan_v2/components/editor_button";
import { FilterResourcePlan } from "@src/features/resource_plan_v2/components/filter_resource_plan";
import { ResourcePlanDownloadButton } from "@src/features/resource_plan_v2/components/resource_plan_download_button";
import { RoleRequestCreationDrawer } from "@src/features/resource_plan_v2/components/role_request_creation_drawer";
import { TableActions } from "@src/features/resource_plan_v2/components/table_actions";
import { TabsResourcePlan } from "@src/features/resource_plan_v2/components/tabs_resource_plan";
import { FilterButton } from "@src/features/table_filtering/components/filter_button";
import { FiltersDrawer } from "@src/features/table_filtering/components/filters_drawer";
import { SearchInput } from "@src/features/table_filtering/components/search_input";
import { TableSettingsButton } from "@src/features/table_filtering/components/table_settings_button";
import { mapToTreeData } from "@src/features/table_filtering/utils/filter_utils";
import {
  setColumns,
  setQuickFilter,
  setTotalDraftRequests,
} from "@src/services/resourcePlanSliceV2";
import { useGetProjectQuery } from "@src/services/slices/projectsSlice";
import { useAppDispatch, useAppSelector, RootState } from "@src/setupStore";
import { TableSettingsConfig } from "@src/types";
import { RoleRequest } from "@src/types/role_request_types";
import { ColDef } from "ag-grid-community";
import Cookies from "universal-cookie";

const cookies = new Cookies();

interface ResourcePlanToolBarProps {
  isFetching: boolean;
  isDisabled: boolean;
  roleRequestData: RoleRequest[];
  columnDefs: ColDef[];
  projectId: string;
  isProjectEnded?: boolean;
}

const ResourcePlanToolBar: React.FC<ResourcePlanToolBarProps> = ({
  isFetching,
  isDisabled,
  roleRequestData,
  columnDefs,
  projectId,
  isProjectEnded = false,
}) => {
  const dispatch = useAppDispatch();
  const { data: project } = useGetProjectQuery(projectId.toString());

  const {
    resourcePlanTableState: { quickFilter },
  } = useAppSelector((state: RootState) => state.resourcePlanSliceV2);

  useEffect(() => {
    if (!roleRequestData) return;
    const totalDrafts: number = roleRequestData?.filter(
      (roleRequest: RoleRequest) => roleRequest.status === REQUEST_STATUS.DRAFT
    ).length;
    dispatch(setTotalDraftRequests(totalDrafts));
  }, [roleRequestData]);

  const [openFilterDrawer, setOpenFilterDrawer] =
    React.useState<boolean>(false);

  const isTableLoading: boolean = isFetching || isDisabled;

  /**
   * Checks if the logged in user is the project manager of the project.
   */
  function isUserProjectManager(): boolean {
    const loggedInEmployeeId: number = cookies?.get("loggedInuser")?.employeeId;
    return loggedInEmployeeId === project.projectManager.employeeId;
  }
  /**
   * Builds the children for the Filters.
   *
   * @returns {React.ReactNode} - Filter Component (content of drawer).
   */
  function renderFilters(): React.ReactNode {
    return (
      <FilterResourcePlan
        disabled={isFetching}
        standardRoleNames={mapToTreeData(
          roleRequestData,
          "standardRoleName",
          true
        )}
      />
    );
  }

  /**
   * Build the initial settings for this table
   *
   * @returns {TableSettingsConfig} - The initial table settings
   */
  function getTableSettings(): TableSettingsConfig {
    const columnsToHide: string[] = columnDefs
      .filter((colDef: ColDef) => colDef.hide)
      .map((colDef: ColDef) => colDef.headerName);

    return {
      columns: columnDefs,
      onChange: (columns: string[]) => {
        dispatch(setColumns(columns));
        const selectedBasicColumn: string[] = [];
        const selectedYearColumns: string[] = [];

        //check if selected column is a year column
        for (const column of columns) {
          if (parseInt(column)) {
            selectedYearColumns.push(column);
          } else {
            selectedBasicColumn.push(column);
          }
        }

        localStorage.setItem(
          "resourcePlanBasicColumns",
          selectedBasicColumn.toString()
        );
        updateStoredYearSettings(selectedYearColumns);
      },
      selectedColumns: [
        ...getBasicColumnSettings(),
        ...getYearColumnSettings(),
      ],
      columnsToHide: columnsToHide,
      defaultHiddenColumns: ["Project Identifier", "Specific Role", "Scope"],
    };
  }

  /**
   * Get the basic column settings of the table like the Requested Role, Project Identifier
   * Specific Role, Scope and Status
   *
   * @return all the selected basic columns or when no basic columns were selected the default settings.
   */
  function getBasicColumnSettings(): string[] {
    const defaultSelectedBasicColumns: string[] = ["Requested Role", "Status"];

    const cachedSelectedBasicColumns: string[] = localStorage
      .getItem("resourcePlanBasicColumns")
      ?.split(",");

    return cachedSelectedBasicColumns
      ? cachedSelectedBasicColumns
      : defaultSelectedBasicColumns;
  }

  /**
   * Get the selected year column settings of the table.
   * By default, all years will be shown until the project end date.
   *
   * @return all the selected year columns or when no year columns were selected
   * all years until the project end date will be returned.
   */
  function getYearColumnSettings(): string[] {
    const allYearColumns: string[] = columnDefs
      .filter((columnDef: ColDef) => parseInt(columnDef.headerName))
      ?.map((columnDef: ColDef) => columnDef.headerName);

    const cachedSelectedYearColumns: string = localStorage.getItem(
      "selectedYearsPerProject"
    );

    const projectYearColumnKey = `project_${projectId}_years`;

    if (cachedSelectedYearColumns) {
      const restoredMap: Map<string, string[]> = new Map(
        JSON.parse(cachedSelectedYearColumns)
      );
      const selectedProjectYears: string[] =
        restoredMap.get(projectYearColumnKey);

      if (selectedProjectYears) return restoredMap.get(projectYearColumnKey);
    }

    return allYearColumns;
  }

  /**
   * Stores the selected year settings in the localStorage. The settings will be stored as a map
   * individual for every project.
   *
   * @param selectedYears array containing all selected years
   */
  function updateStoredYearSettings(selectedYears: string[]) {
    // Load
    const stored = JSON.parse(localStorage.getItem("selectedYearsPerProject"));
    const map: Map<string, string[]> = new Map(stored);

    // Update entry
    map.set(`project_${projectId}_years`, selectedYears);

    // Save again
    localStorage.setItem("selectedYearsPerProject", JSON.stringify([...map]));
  }

  return (
    <div className="tool-bar">
      <div className="tool-bar__button-section">
        <TableActions
          roleRequestData={roleRequestData}
          disabled={isTableLoading}
        />
        <SearchInput
          defaultValue={quickFilter}
          disabled={isTableLoading}
          onChange={(value: string) => dispatch(setQuickFilter(value))}
        />
      </div>
      <div className="tool-bar__button-section">
        <TabsResourcePlan disabled={isTableLoading} />
        <div>
          <ResourcePlanDownloadButton
            project={project}
            disabled={isTableLoading}
          />
          <EditorButton
            disabled={!isUserProjectManager() || isTableLoading}
            projectId={projectId}
            isUserProjectManager={isUserProjectManager()}
          />
          <TableSettingsButton
            tableSettings={getTableSettings()}
            disabled={isTableLoading}
          />
          <FilterButton
            setOpenFilterDrawer={() => setOpenFilterDrawer(true)}
            disabled={isTableLoading}
          />
          <RoleRequestCreationDrawer
            projectId={projectId}
            isDisabled={isTableLoading || isProjectEnded}
            isProjectEnded={isProjectEnded}
          />
        </div>
      </div>
      <FiltersDrawer
        isDrawerOpen={openFilterDrawer}
        setIsDrawerOpen={setOpenFilterDrawer}
        drawerTitle="Filter Already requested roles"
        filters={renderFilters()}
      />
    </div>
  );
};

export default ResourcePlanToolBar;
